import React, { ChangeEvent } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { bindActionCreators, Dispatch } from 'redux';

import * as inventoryAssetActionCreators from '@atom/actions/inventoryAssetActions';
import * as inventorySchemaActionCreators from '@atom/actions/inventorySchemaActions';
import { Modal, Progress } from '@atom/mui';
import { getFilteredSchemaByMarkerId } from '@atom/selectors/schemaSelectors';
import {
  InventoryAssetActions,
  InventorySchemaActions,
} from '@atom/types/actions';
import { ElementGroupsItem } from '@atom/types/inventory';
import { InventorySchemaState, ReduxStore } from '@atom/types/store';

import NoElementsDialog from './NoElementsDialog';
import SchemaElementGroupSection from './SchemaElementGroupSection';
import SchemaElementListRow from './SchemaElementListRow';

import classes from './elements.module.css';

const setInitialState = (): any => ({
  open: false,
  selectedItems: new Set([]),
  schemaQuantities: {},
});

type ReduxStateProps = {
  schema: InventorySchemaState;
  loadingBatchCreation: boolean;
  loadingInventorySchema: boolean;
  loadingElementDelete: boolean;
  loadingSchemaTreeNode: boolean;
};

type PassedProps = {
  open?: boolean;
  closeModal?: () => void;
  updateSelectedItem: (item: Object, type: string) => void;
  title: string;
  assetId: string;
  assetName: string;
  markerId: string;
  onBatchCreate: (payload: Object) => void;
};

type ReduxDispatchProps = {
  inventoryAssetActions: InventoryAssetActions;
  inventorySchemaActions: InventorySchemaActions;
};

type Props = PassedProps & ReduxStateProps & ReduxDispatchProps;

type State = {
  open: boolean;
  selectedItems: any;
  schemaQuantities: any;
};

class AddAssetElementModal extends React.Component<Props, State> {
  state = { ...setInitialState(), open: this.props.open };

  UNSAFE_componentWillReceiveProps(nextProps: any) {
    if (this.props.loadingBatchCreation && !nextProps.loadingBatchCreation) {
      this.closeModal();
    }

    if (this.state.open !== nextProps.open) {
      this.openModal(nextProps.open);
    }
  }

  closeModal = (): (() => void) => {
    const { closeModal, updateSelectedItem, inventorySchemaActions, schema } =
      this.props;
    this.setState(setInitialState);

    updateSelectedItem({}, 'asset');

    if (!R.isEmpty(schema)) {
      inventorySchemaActions.requestInventorySchemaClear();
    }

    // @ts-ignore
    return closeModal();
  };

  openModal = (open: boolean) => {
    const { assetId, inventorySchemaActions } = this.props;

    inventorySchemaActions.retrieveInventorySchemaByAssetId({ id: assetId });

    this.setState({
      open,
    });
  };

  confirm = (): (() => void) => {
    const { schema, assetId, inventoryAssetActions, onBatchCreate } =
      this.props;
    const { schemaQuantities, selectedItems } = this.state;
    const elements = [...selectedItems].reduce(
      (previous: any[], next: any): any => {
        const pathAndId = next.split('.');
        const schemaId = R.last(pathAndId);
        const path = R.dropLast(1, pathAndId);

        const batchItem = {
          schemaId,
          quantity: schemaQuantities[next] || 1,
        };

        if (R.isEmpty(path)) {
          return [...previous, batchItem];
        }

        const indexPath = path.reduce(
          (previousPath: any[], nextPathId: string): string[] => {
            const lens = R.lensPath(previousPath);
            const section = R.view(lens, previous);

            // @ts-ignore
            const nextIndex = section.findIndex(
              (item: any): boolean => item.schemaId === nextPathId,
            );
            return [...previousPath, nextIndex, 'elements'];
          },
          [],
        );

        // @ts-ignore
        const lens = R.lensPath(indexPath);

        const view = R.view(lens, previous) || [];
        // @ts-ignore
        return R.set(lens, [...view, batchItem], previous);
      },
      [],
    );

    const batchSchemaRequestBody = {
      assetId,
      assetSchemaId: schema.id,
      elements,
    };

    if (!R.isNil(onBatchCreate)) {
      onBatchCreate(batchSchemaRequestBody);
    } else {
      inventoryAssetActions.requestElementBatchCreate(batchSchemaRequestBody);
    }

    return this.closeModal();
  };

  setSchemaQuantity = (input: string): number => {
    const parsedValue = Math.floor(Number(input));
    if (parsedValue > 99) {
      return 99;
    }
    if (parsedValue < 1) {
      return 1;
    }

    return parsedValue;
  };

  onChange = (
    event: ChangeEvent<HTMLInputElement>,
    name: string,
    value: any,
  ): void => {
    const { schemaQuantities } = this.state;
    const schemaQuantity = this.setSchemaQuantity(value);

    return this.setState({
      schemaQuantities: { ...schemaQuantities, [name]: schemaQuantity },
    });
  };

  toggleChecked = (id: string): void => {
    const { selectedItems } = this.state;

    const selectedItemsSet = new Set([...selectedItems]);

    if (selectedItemsSet.has(id)) {
      selectedItemsSet.delete(id);
      const selectedItemsWithRemovals = new Set(
        [...selectedItemsSet].filter(
          (item: any): boolean => !item.startsWith(id),
        ),
      );
      return this.setState({ selectedItems: selectedItemsWithRemovals });
    }
    selectedItemsSet.add(id);
    return this.setState({ selectedItems: selectedItemsSet });
  };

  renderElements = () => {
    const { schema, loadingSchemaTreeNode, inventorySchemaActions } =
      this.props;
    const { selectedItems, schemaQuantities } = this.state;

    if (R.isEmpty(schema) || R.isEmpty(schema.elementGroups)) {
      return <div />;
    }

    const elementGroups = schema.elementGroups;

    return elementGroups.map(
      (elementGroup: ElementGroupsItem, elementGroupIndex: number) => {
        if (elementGroup.isGroup) {
          return (
            <>
              {/* 
                // @ts-ignore */}
              <SchemaElementGroupSection
                key={elementGroup.name}
                elementGroupIndex={elementGroupIndex}
                inventorySchemaActions={inventorySchemaActions}
                loadingSchemaTreeNode={loadingSchemaTreeNode}
                onClick={this.toggleChecked}
                onChange={this.onChange}
                elementGroup={elementGroup}
                selectedItems={selectedItems}
                schemaQuantities={schemaQuantities}
              />
            </>
          );
        }

        const element = elementGroup.elements[0];

        return (
          <div className={classes.elementListRowContainer} key={element.id}>
            <SchemaElementListRow
              isChecked={selectedItems.has(element.id)}
              onChange={this.onChange}
              loading={loadingSchemaTreeNode}
              // @ts-ignore
              element={element}
              inventorySchemaActions={inventorySchemaActions}
              onClick={this.toggleChecked}
              value={schemaQuantities[element.id]}
              selectedItems={selectedItems}
              schemaQuantities={schemaQuantities}
              elementPath={['elementGroups', elementGroupIndex, 'elements', 0]}
            />
          </div>
        );
      },
    );
  };

  render() {
    const {
      title,
      assetName,
      loadingBatchCreation,
      loadingInventorySchema,
      loadingElementDelete,
      schema,
    } = this.props;
    const { open, selectedItems } = this.state;
    const addModalDisabledState = !selectedItems.size;

    const dialogContent =
      loadingElementDelete ||
      loadingInventorySchema ||
      loadingBatchCreation ||
      R.isEmpty(schema) ? (
        <div className={classes.elementModalSpinnerContainer}>
          <Progress />
        </div>
      ) : (
        <div className={classes.elementModalContainer}>
          <div className={classes.elementModalSubheading}>
            Select elements and quantities from inventory type for
            <span className={classes.elementModalAssetName}>{assetName}</span>
          </div>
          <div className={classes.elementModalSelectionBackdrop}>
            {this.renderElements()}
          </div>
        </div>
      );

    const dialog = R.isEmpty(schema.elementGroups) ? (
      <NoElementsDialog
        title="No elements available."
        assetName={assetName}
        closeModal={this.closeModal}
        open={open}
      />
    ) : (
      <Modal
        title={title}
        contentStyle={{ padding: '0rem' }}
        confirmButtonText="Add"
        onConfirm={this.confirm}
        onCancel={this.closeModal}
        disabled={addModalDisabledState}
        open={open}
      >
        {dialogContent}
      </Modal>
    );

    return <div>{dialog}</div>;
  }
}

export const mapStateToProps = (
  state: ReduxStore,
  ownProps: Props,
): ReduxStateProps => ({
  // @ts-ignore
  schema: getFilteredSchemaByMarkerId(state, ownProps),
  loadingInventorySchema: state.loading.loadingInventorySchema,
  loadingBatchCreation: state.loading.loadingElementBatchCreation,
  loadingElementDelete: state.loading.loadingElementDelete,
  loadingSchemaTreeNode: state.loading.loadingSchemaTreeNode,
});

const mapDispatchToProps = (dispatch: Dispatch): ReduxDispatchProps => ({
  inventorySchemaActions: bindActionCreators(
    inventorySchemaActionCreators,
    dispatch,
  ),
  inventoryAssetActions: bindActionCreators(
    inventoryAssetActionCreators,
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddAssetElementModal);
